<template>
  <v-card flat data-testid="regimen_grid_sequence_edit">
    <v-card-text>
      <v-alert v-if="!selected_sequence.sequence.length" type="error" outlined dense>
        {{ $t("regimen_container_edit.snapshot_missing") }}
      </v-alert>
      <template v-else>
        <v-layout row wrap>
          <v-flex xs12 md8 class="pr-md-6">
            <h3 class="mb-3">{{ selected_sequence.title }}</h3>
            <p v-if="selected_sequence.description">{{ selected_sequence.description }}</p>
            <v-select
              :value="localValue.active_step"
              outlined
              :items="starting_step_options"
              :label="$t('regimen_grid_sequence_edit.starting_step')"
              :rules="[(value) => active_step_valid(value) || $t('regimen_grid_sequence_edit.starting_step_required')]"
              @input="set_active_step"
            >
              <template v-slot:item="data">
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.text }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-select>
          </v-flex>
          <v-flex xs12 md4>
            <RegimenGridCustomizationPanel
              :status="customization_status"
              :can-update-from-source="can_update_from_source"
              @customize="sequence_edit_dialog = true"
              @update-from-source="update_from_source"
            />
          </v-flex>
        </v-layout>

        <v-alert v-if="error_message" dense outlined type="error">
          {{ error_message }}
        </v-alert>
      </template>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="!active_step_valid(localValue.active_step)"
        data-testid="regimen_grid_sequence_edit_save"
        @click="save"
      >
        {{ $t("common.save") }}
      </v-btn>
    </v-card-actions>

    <v-dialog v-model="sequence_edit_dialog" max-width="1200" :fullscreen="small_screen || phone_screen">
      <GridSequenceEdit
        v-if="sequence_edit_dialog"
        :existing_sequence="selected_sequence"
        :assignment="localValue"
        :clinic_id="selected_sequence.clinic_id || ''"
        @grid-sequence-updated="sequence_updated"
        @close="sequence_edit_dialog = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import { sequenceStepTitle } from "../classes/grid_sequence";
import RegimenGridCustomizationPanel from "./RegimenGridCustomizationPanel.vue";
import {
  cloneSourceOntoAssignment,
  gridCustomizationStatus,
  resolveSourceGrid,
} from "../lib/regimen_grid_customization";

export default {
  components: {
    GridSequenceEdit: () => import("./GridSequenceEdit.vue"),
    RegimenGridCustomizationPanel,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      error_message: "",
      sequence_edit_dialog: false,
    };
  },
  computed: {
    ...mapState(["grid_sequences", "small_screen", "phone_screen"]),
    localValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    selected_sequence() {
      if (this.localValue.grid_sequence && this.localValue.grid_sequence.id === this.localValue.exercise_id) {
        return this.localValue.grid_sequence;
      }
      return this.grid_sequences.find((sequence) => sequence.id === this.localValue.exercise_id) || { sequence: [] };
    },
    source_grid() {
      return resolveSourceGrid(this.localValue, { grid_sequences: this.grid_sequences });
    },
    customization_status() {
      return gridCustomizationStatus(this.localValue, this.source_grid);
    },
    can_update_from_source() {
      return Boolean(
        this.source_grid
        && (this.customization_status === "customized" || this.customization_status === "out_of_date"),
      );
    },
    starting_step_ids() {
      return this.selected_sequence.sequence.map((step) => step.id);
    },
    starting_step_options() {
      const options = this.selected_sequence.sequence.map((step, index) => ({
        text: sequenceStepTitle(step, this.$t("grid_sequence_step_list.step", { number: index + 1 })),
        value: step.id,
      }));
      if (this.localValue && this.localValue.active_step === "complete") {
        options.push({
          text: this.$t("grid_sequence_summary.sequence_complete"),
          value: "complete",
        });
      }
      return options;
    },
  },
  methods: {
    active_step_valid(active_step) {
      return active_step === "complete" || this.starting_step_ids.includes(active_step);
    },
    set_active_step(active_step) {
      if (this.localValue.active_step === active_step) {
        return;
      }
      Vue.set(this.localValue, "active_step", active_step);
      Vue.set(this.localValue, "active_step_activation_id", null);
      Vue.set(this.localValue, "active_step_activated_at", null);
    },
    sequence_updated(sequence) {
      Vue.set(this.localValue, "grid_sequence", JSON.parse(JSON.stringify(sequence)));
      Vue.set(this.localValue, "grid_customized", true);
      this.error_message = "";
      this.sequence_edit_dialog = false;
    },
    update_from_source() {
      if (!this.source_grid) return;
      cloneSourceOntoAssignment(this.localValue, this.source_grid);
      Vue.set(this.localValue, "grid_sequence", this.localValue.grid_sequence);
      Vue.set(this.localValue, "grid_customized", false);
      this.error_message = "";
    },
    save() {
      if (!this.active_step_valid(this.localValue.active_step)) {
        this.error_message = this.$t("regimen_grid_sequence_edit.active_step_missing");
        return;
      }
      this.error_message = "";
      this.$emit("save");
    },
  },
};
</script>
